import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Modal from './Modal';
import HolderSelect from './HolderSelect';
import { contractorAuth } from '../lib/contractorAuth';
import { holderFields, holderLabel, deviceName, LIFECYCLE_LABEL, lifecycleTone, tagText } from '../lib/assets';
import './Assets.css';

/**
 * A storage area's contents, by cupboard and shelf (104).
 *
 * A storage area holds tens or hundreds of devices, connected and not: too
 * many to pin, so the plan carries ONE icon for it and this panel is what
 * opens. Devices are grouped by container (a cupboard or shelving unit) and
 * then shelf, each heading with its count and "n / capacity" when a capacity
 * is set. A stored device may belong to nobody or still to a person (a spare
 * kept for them); one that reported in during the last three days while
 * stored is flagged "still reporting" - it may have been taken out and not
 * recorded.
 *
 * Tick devices to Take out (to a person and/or a place, through the same
 * assign/move calls as the device page) or Move to shelf. "Put away here"
 * searches the register and posts the chosen devices to the storage, unless
 * "Still belongs to ..." is ticked, in which case their holder keeps them.
 */
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const errorOf = (e, fallback) => e?.response?.data?.error || fallback;

const KIND_WORDS = { storage: 'Storage area', container: 'Cupboard or shelving unit', shelf: 'Shelf' };

const countText = (n, capacity) => (capacity ? `${n} / ${capacity}` : `${n} device${n === 1 ? '' : 's'}`);

export default function StoragePanel({ placeId, meta, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [note, setNote] = useState('');
  const [query, setQuery] = useState('');
  const [kind, setKind] = useState('');
  const [picked, setPicked] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  // Take out / Move to shelf controls.
  const [holder, setHolder] = useState('');
  const [outPlace, setOutPlace] = useState('');
  const [shelf, setShelf] = useState('');
  // Put away here.
  const [putting, setPutting] = useState(false);
  const [find, setFind] = useState('');
  const [found, setFound] = useState([]);
  const [chosen, setChosen] = useState({});
  const [keepHolder, setKeepHolder] = useState(false);

  const load = useCallback(() => axios.get(`${API_BASE}/assets/storage/${placeId}`, contractorAuth())
    .then((r) => { setData(r.data); setError(''); })
    .catch((e) => setError(errorOf(e, 'Could not load this storage area.'))), [placeId]);
  useEffect(() => { load(); }, [load]);

  const containers = useMemo(() => data?.containers || [], [data]);
  const everyDevice = useMemo(() => {
    const list = [...(data?.devices || [])];
    containers.forEach((c) => {
      list.push(...(c.devices || []));
      (c.shelves || []).forEach((s) => list.push(...(s.devices || [])));
    });
    return list;
  }, [data, containers]);
  const kinds = useMemo(
    () => [...new Set(everyDevice.map((d) => d.category_name).filter(Boolean))].sort(),
    [everyDevice]);

  const q = query.trim().toLowerCase();
  const keeps = (d) => (!kind || d.category_name === kind) && (!q
    || [d.asset_tag, d.manufacturer, d.model, d.category_name, d.holder_name].filter(Boolean)
      .join(' ').toLowerCase().includes(q));

  const tick = (id) => setPicked((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const run = async (fn, okText) => {
    setBusy(true);
    setError('');
    setNote('');
    try {
      const out = await fn();
      await load();
      if (onChanged) onChanged();
      if (okText) setNote(okText(out));
      return true;
    } catch (e) {
      setError(errorOf(e, 'That did not work.'));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const takeOut = async () => {
    const ids = [...picked];
    const ok = await run(async () => {
      for (const id of ids) {
        if (holder) {
          // eslint-disable-next-line no-await-in-loop
          await axios.post(`${API_BASE}/assets/${id}/assign`,
            { ...holderFields(holder, { action: true }), ...(outPlace ? { location_id: outPlace } : {}), reason: 'Taken out of storage' }, contractorAuth());
        } else {
          // eslint-disable-next-line no-await-in-loop
          await axios.post(`${API_BASE}/assets/${id}/move`, { location_id: outPlace, reason: 'Taken out of storage' }, contractorAuth());
        }
      }
      return ids.length;
    }, (n) => `${n} device${n === 1 ? '' : 's'} taken out.`);
    if (ok) { setPicked(new Set()); setHolder(''); setOutPlace(''); }
  };

  const toShelf = async () => {
    const ids = [...picked];
    const ok = await run(async () => {
      for (const id of ids) {
        // eslint-disable-next-line no-await-in-loop
        await axios.post(`${API_BASE}/assets/${id}/move`, { location_id: shelf, reason: 'Moved within storage' }, contractorAuth());
      }
      return ids.length;
    }, (n) => `${n} device${n === 1 ? '' : 's'} moved.`);
    if (ok) { setPicked(new Set()); setShelf(''); }
  };

  // Put away: search the register (after a pause) for devices not already here.
  useEffect(() => {
    if (!putting) return undefined;
    const t = setTimeout(() => {
      axios.get(`${API_BASE}/assets`, { params: { q: find.trim(), limit: 20 }, ...(contractorAuth() || {}) })
        .then((r) => setFound(r.data?.assets || []))
        .catch(() => setFound([]));
    }, 250);
    return () => clearTimeout(t);
  }, [putting, find]);

  const here = new Set(everyDevice.map((d) => d.id));
  const offered = found.filter((a) => !here.has(a.id));
  const chosenList = Object.values(chosen);
  const holders = [...new Set(chosenList.map((a) => holderLabel(a)).filter(Boolean))];
  const toggleChosen = (a) => setChosen((c) => {
    const next = { ...c };
    if (next[a.id]) delete next[a.id]; else next[a.id] = a;
    return next;
  });

  const putAway = async () => {
    const ok = await run(async () => {
      const r = await axios.post(`${API_BASE}/assets/storage/${placeId}/put-away`,
        { asset_ids: chosenList.map((a) => a.id), keep_holder: keepHolder }, contractorAuth());
      return r.data;
    }, (r) => {
      const done = (r?.done || []).length;
      const refused = r?.refused || [];
      return `${done} device${done === 1 ? '' : 's'} put away`
        + (refused.length ? `; not put away: ${refused.map((x) => `${x.asset_tag} (${x.reason})`).join(', ')}.` : '.');
    });
    if (ok) { setChosen({}); setKeepHolder(false); setPutting(false); setFind(''); }
  };

  const write = Boolean(data?.write);

  const row = (d) => (
    <li key={d.id} className="as-store-row">
      {write && (
        <input type="checkbox" className="as-select" checked={picked.has(d.id)} onChange={() => tick(d.id)}
          aria-label={`Choose ${tagText(d)}`} />
      )}
      <Link to={`/assets/${d.id}`} className="as-tag">{tagText(d)}</Link>
      <span>{deviceName(d)}</span>
      <span className="as-quiet">{d.holder_name ? `still ${holderLabel(d)}’s` : 'nobody’s'}</span>
      <span className="as-chip" data-tone={lifecycleTone(d.lifecycle_status)}>{LIFECYCLE_LABEL[d.lifecycle_status] || d.lifecycle_status}</span>
      {d.reporting && <span className="as-chip" data-tone="warning" title="It reported in recently while stored">still reporting</span>}
    </li>
  );

  const list = (devices) => {
    const shown = (devices || []).filter(keeps);
    return shown.length ? <ul className="as-tree">{shown.map(row)}</ul> : null;
  };

  const heading = (c, level) => {
    const Tag = level === 1 ? 'h3' : 'h4';
    return (
      <Tag className="as-store-head">
        {c.name}
        {KIND_WORDS[c.kind] && <span className="as-quiet">{` · ${KIND_WORDS[c.kind]}`}</span>}
        <span className="as-chip" data-tone="idle">{countText(c.count ?? (c.devices || []).length, c.capacity)}</span>
      </Tag>
    );
  };

  const shelfChoices = containers.flatMap((c) => [
    { id: c.id, label: c.name },
    ...(c.shelves || []).map((s) => ({ id: s.id, label: `${c.name} › ${s.name}` }))
  ]);
  const places = (meta?.locations || []).filter((l) => l.active);

  return (
    <Modal wide title={data ? data.place.name : 'Storage'} subtitle={data ? data.place.path : null} onClose={onClose}
      footer={(
        <>
          {data && write && (
            <>
              <Link className="btn btn-secondary" to={`/assets/stocktake/${placeId}`}>Stocktake</Link>
              <Link className="btn btn-secondary" to={`/assets/places/${placeId}/labels`}>Print shelf labels</Link>
            </>
          )}
          {data && !write && (
            <Link className="btn btn-secondary" to={`/assets/stocktake/${placeId}`}>Stocktake</Link>
          )}
          <button type="button" className="btn btn-primary" onClick={onClose}>Close</button>
        </>
      )}>
      {error && <p className="as-error" role="alert">{error}</p>}
      {note && <p className="as-note" data-tone="info" role="status">{note}</p>}
      {!data && !error && <p className="as-quiet">Loading…</p>}
      {data && (
        <div data-testid="storage-panel">
          <div className="as-toolbar">
            <input type="search" value={query} onChange={(e) => setQuery(e.target.value)}
              placeholder="Tag, make, model or person" aria-label="Search this storage area" />
            <select value={kind} onChange={(e) => setKind(e.target.value)} aria-label="Kind of device">
              <option value="">Every kind</option>
              {kinds.map((k) => <option key={k} value={k}>{k}</option>)}
            </select>
            <span className="as-spacer" />
            <span className="as-quiet">{`${data.total} device${data.total === 1 ? '' : 's'}${data.place.capacity ? ` · capacity ${data.place.capacity}` : ''}`}</span>
            {write && (
              <button type="button" className="btn btn-primary" onClick={() => setPutting((v) => !v)}>Put away here</button>
            )}
          </div>

          {putting && write && (
            <div className="as-bulk" data-testid="put-away" style={{ display: 'block' }}>
              <input type="search" value={find} onChange={(e) => setFind(e.target.value)}
                placeholder="Find a device to put away" aria-label="Find a device to put away" />
              <ul className="as-tree">
                {offered.map((a) => (
                  <li key={a.id}>
                    <label className="as-check">
                      <input type="checkbox" checked={Boolean(chosen[a.id])} onChange={() => toggleChosen(a)} />
                      <strong>{tagText(a)}</strong>
                      <span>{deviceName(a)}</span>
                      <span className="as-quiet">{[holderLabel(a) ? `with ${holderLabel(a)}` : null, a.location_path].filter(Boolean).join(' · ')}</span>
                    </label>
                  </li>
                ))}
                {offered.length === 0 && <li className="as-quiet">No other devices found.</li>}
              </ul>
              <label className="as-check">
                <input type="checkbox" checked={keepHolder} onChange={(e) => setKeepHolder(e.target.checked)} />
                {`Still belongs to ${holders.length ? holders.join(', ') : 'its holder'}`}
              </label>
              <button type="button" className="btn btn-primary btn-small" disabled={busy || chosenList.length === 0} onClick={putAway}>
                {`Put away ${chosenList.length || ''}`.trim()}
              </button>
            </div>
          )}

          {write && picked.size > 0 && (
            <div className="as-bulk" data-testid="storage-bulk">
              <strong>{`${picked.size} ticked`}</strong>
              <label htmlFor="as-st-holder" className="as-quiet">Take out to</label>
              <HolderSelect id="as-st-holder" meta={meta} value={holder} none="Nobody in particular" onChange={setHolder} />
              <select value={outPlace} aria-label="Take out to place" onChange={(e) => setOutPlace(e.target.value)}>
                <option value="">Choose a place…</option>
                {places.map((l) => <option key={l.id} value={l.id}>{l.path}</option>)}
              </select>
              <button type="button" className="btn btn-primary btn-small" disabled={busy || (!holder && !outPlace)} onClick={takeOut}>Take out</button>
              <label htmlFor="as-st-shelf" className="as-quiet">or</label>
              <select id="as-st-shelf" value={shelf} onChange={(e) => setShelf(e.target.value)}>
                <option value="">Move to shelf…</option>
                {shelfChoices.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
              <button type="button" className="btn btn-secondary btn-small" disabled={busy || !shelf} onClick={toShelf}>Move to shelf</button>
            </div>
          )}

          {data.total === 0 && <p className="as-quiet">Nothing is stored here yet.</p>}
          {(data.devices || []).filter(keeps).length > 0 && (
            <section aria-label="Loose in the storage area">
              <h3 className="as-store-head">{`Loose in ${data.place.name}`}</h3>
              {list(data.devices)}
            </section>
          )}
          {containers.map((c) => (
            <section key={c.id} className="as-store-group" aria-label={c.name}>
              {heading(c, 1)}
              {list(c.devices)}
              {(c.shelves || []).map((s) => (
                <div key={s.id} className="as-store-shelf" role="group" aria-label={`${c.name} ${s.name}`}>
                  {heading(s, 2)}
                  {list(s.devices)}
                </div>
              ))}
            </section>
          ))}
        </div>
      )}
    </Modal>
  );
}
